import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Calendar, CheckCircle, Clock, Mail, Send, Users, Eye,
  MousePointerClick, Loader2, RefreshCw, Trash2, Pencil,
  Copy, Smartphone, Monitor, Search, Zap, AlertTriangle,
  Paperclip, FileText, Image, Download, File, ExternalLink, UserX
} from 'lucide-react';
import { campaignApi } from '../services/emailApi';
import { useToast } from '../shared/ToastProvider';
import ConfirmModal from '../shared/ConfirmModal';
import { useWallet } from '../../../context/walletContext';
import './EmailCampaignDetail.v3.css';

/* ── Build full email HTML from the nested template object returned by the campaign API ── */
const buildCampaignPreviewHtml = (tpl) => {
  if (!tpl) return '';
  const header  = tpl.header || {};
  const body    = tpl.body   || {};
  const cta     = tpl.cta    || {};
  const footer  = tpl.footer || {};
  const social  = tpl.social_links || {};

  const logoUrl   = header.logo_url || '';
  const bannerUrl = header.banner_image_url || '';
  const bgColor   = header.background_color || '#ffffff';
  const logoPos   = header.logo_position || 'center';
  const footerImg = footer.image_url || '';

  /* Header */
  let headerHtml = '';
  if (header.title || logoUrl || bannerUrl) {
    headerHtml = `<div style="background-color:${bgColor};padding:24px 20px;text-align:${logoPos};overflow:hidden">
      ${logoUrl ? `<img src="${logoUrl}" alt="Logo" style="max-height:60px;max-width:200px;width:auto;height:auto;display:${logoPos === 'center' ? 'block' : 'inline-block'};${logoPos === 'center' ? 'margin:0 auto' : logoPos === 'right' ? 'margin-left:auto' : ''}">` : ''}
      ${header.title ? `<h1 style="margin:12px 0 0;font-size:22px;color:#1e293b">${header.title}</h1>` : ''}
      ${bannerUrl ? `<img src="${bannerUrl}" alt="Banner" style="width:100%;max-width:100%;height:auto;margin-top:16px;border-radius:8px;display:block;object-fit:contain;${logoPos === 'center' ? 'margin-left:auto;margin-right:auto' : ''}">` : ''}
    </div>`;
  }

  /* Body */
  let bodyHtml = '<div style="padding:24px 20px">';
  if (body.greeting) {
    bodyHtml += `<p style="font-size:16px;color:#334155;margin:0 0 16px">${body.greeting}</p>`;
  }
  const content = body.content || tpl.body_html || '';
  bodyHtml += `<div style="color:#475569;line-height:1.7;font-size:15px">${content || '<p style="color:#94a3b8;font-style:italic">No body content.</p>'}</div>`;
  bodyHtml += '</div>';

  /* CTA */
  let ctaHtml = '';
  if (cta.button_text && cta.button_url) {
    const btnBg = cta.button_bg_color || '#007bff';
    ctaHtml = `<div style="padding:0 20px 24px;text-align:center">
      <a href="${cta.button_url}" style="display:inline-block;padding:12px 28px;background:${btnBg};color:#fff;border-radius:6px;text-decoration:none;font-weight:600;font-size:15px">${cta.button_text}</a>
    </div>`;
  }

  /* Footer */
  let footerHtml = '';
  const hasSocial = Object.values(social).some(Boolean);
  const hasFooter = footer.signature || footer.website_url || footer.slogan || footer.company_address || footer.show_unsubscribe || footerImg || hasSocial;
  if (hasFooter) {
    footerHtml = '<div style="border-top:1px solid #e5e7eb;padding:24px 20px;text-align:center;background:#f8fafc">';
    if (footerImg) {
      footerHtml += `<img src="${footerImg}" alt="Footer Logo" style="max-height:40px;max-width:180px;width:auto;height:auto;margin-bottom:12px;display:block;margin-left:auto;margin-right:auto;object-fit:contain">`;
    }
    if (footer.signature) {
      footerHtml += `<p style="font-size:14px;color:#475569;margin:0 0 8px;white-space:pre-line">${footer.signature}</p>`;
    }
    if (footer.slogan) {
      footerHtml += `<p style="font-size:13px;color:#94a3b8;font-style:italic;margin:0 0 12px">${footer.slogan}</p>`;
    }

    const socialIcons = [];
    if (social.facebook)  socialIcons.push(`<a href="${social.facebook}" style="color:#4f46e5;text-decoration:none;margin:0 6px;font-size:13px">Facebook</a>`);
    if (social.instagram) socialIcons.push(`<a href="${social.instagram}" style="color:#4f46e5;text-decoration:none;margin:0 6px;font-size:13px">Instagram</a>`);
    if (social.twitter)   socialIcons.push(`<a href="${social.twitter}" style="color:#4f46e5;text-decoration:none;margin:0 6px;font-size:13px">Twitter</a>`);
    if (social.linkedin)  socialIcons.push(`<a href="${social.linkedin}" style="color:#4f46e5;text-decoration:none;margin:0 6px;font-size:13px">LinkedIn</a>`);
    if (socialIcons.length) {
      footerHtml += `<div style="margin:8px 0">${socialIcons.join(' · ')}</div>`;
    }
    if (footer.website_url) {
      footerHtml += `<p style="font-size:12px;margin:8px 0 0"><a href="${footer.website_url}" style="color:#4f46e5;text-decoration:none">${footer.website_url}</a></p>`;
    }
    if (footer.company_address) {
      footerHtml += `<p style="font-size:12px;color:#94a3b8;margin:6px 0 0">${footer.company_address}</p>`;
    }
    if (footer.show_unsubscribe) {
      footerHtml += `<p style="font-size:11px;margin:12px 0 0"><a href="#" style="color:#94a3b8;text-decoration:underline">Unsubscribe</a></p>`;
    }
    footerHtml += '</div>';
  }

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<style>body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;margin:0;padding:0;color:#1a1a2e;line-height:1.6;background:#f1f5f9}img{max-width:100%!important;height:auto!important;display:block;object-fit:contain;box-sizing:border-box}table{max-width:100%!important}a{color:#4f46e5}.email-container{max-width:600px;margin:0 auto;background:#fff;border-radius:8px;overflow:hidden;box-shadow:0 1px 3px rgba(0,0,0,0.08);word-wrap:break-word;overflow-wrap:break-word}</style>
</head><body><div class="email-container">${headerHtml}${bodyHtml}${ctaHtml}${footerHtml}</div></body></html>`;
};

const STATUS_MAP = {
  draft:      { label: 'Draft',      badge: 'neutral',  icon: Clock },
  scheduled:  { label: 'Scheduled',  badge: 'info',     icon: Calendar },
  pending:    { label: 'Pending',    badge: 'info',     icon: Clock },
  generating: { label: 'Generating', badge: 'warning',  icon: Zap },
  sending:    { label: 'Sending',    badge: 'warning',  icon: Send },
  paused:     { label: 'Paused',     badge: 'danger',   icon: Clock },
  sent:       { label: 'Sent',       badge: 'success',  icon: CheckCircle },
  completed:  { label: 'Completed',  badge: 'success',  icon: CheckCircle },
  failed:     { label: 'Failed',     badge: 'danger',   icon: AlertTriangle },
};

const StatCard = ({ icon: Icon, label, value, rate, color }) => (
  <div className="ecd-stat-card">
    <div className="ecd-stat-header">
      <span className="ecd-stat-label">{label}</span>
      <div className="ecd-stat-icon-wrap" style={{ color: color }}>
        <Icon size={16} strokeWidth={2.5} />
      </div>
    </div>
    <div className="ecd-stat-value">{value}</div>
    {rate && (
      <div className="ecd-stat-footer">
        <span className="ecd-stat-rate" style={{ background: `${color}1A`, color }}>
          {rate}
        </span>
        <span>vs avg campaign</span>
      </div>
    )}
    <div className="ecd-stat-accent" style={{ background: color }} />
  </div>
);

const TabButton = ({ id, activeTab, setActiveTab, children }) => (
  <button
    className={`ecd-tab ${activeTab === id ? 'active' : ''}`}
    onClick={() => setActiveTab(id)}
  >
    {children}
  </button>
);

const EmailCampaignDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { planStatus, refreshPlanStatus } = useWallet();
  const [refreshing, setRefreshing] = useState(false);

  const [campaign, setCampaign] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [testEmail, setTestEmail] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ open: false, type: '' });
  const [costEstimate, setCostEstimate] = useState(null);
  const [costLoading, setCostLoading] = useState(false);
  const [planQuota, setPlanQuota] = useState(null);
  const [previewMode, setPreviewMode] = useState('desktop');
  const previewIframeRef = useRef(null);

  const [recipients, setRecipients] = useState([]);
  const [recipientsPaging, setRecipientsPaging] = useState({ page: 1, limit: 25, total: 0, totalPages: 0 });
  const [recipientsStatus, setRecipientsStatus] = useState('');
  const [recipientsSearch, setRecipientsSearch] = useState('');
  const [recipientsLoading, setRecipientsLoading] = useState(false);

  const fetchCampaign = useCallback(async () => {
    try {
      const res = await campaignApi.getById(id);
      if (res.data?.success) setCampaign(res.data.data);
    } catch (err) {
      toast.error('Failed to load campaign');
      navigate('/dashboard/email/campaigns');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, toast]);

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await campaignApi.analytics(id);
      if (res.data?.success) setAnalytics(res.data.data);
    } catch {}
  }, [id]);

  const fetchRecipients = useCallback(async (page = 1) => {
    setRecipientsLoading(true);
    try {
      const params = { page, limit: recipientsPaging.limit };
      if (recipientsStatus) params.status = recipientsStatus;
      if (recipientsSearch.trim()) params.search = recipientsSearch.trim();
      const res = await campaignApi.recipients(id, params);
      if (res.data?.success) {
        const payload = res.data.data || {};
        const list = payload.transactions || payload.recipients || payload.items || [];
        setRecipients(Array.isArray(list) ? list : []);
        setRecipientsPaging(prev => ({ ...prev, ...(payload.pagination || {}), page }));
      }
    } catch {} finally {
      setRecipientsLoading(false);
    }
  }, [id, recipientsPaging.limit, recipientsStatus, recipientsSearch]);

  useEffect(() => { fetchCampaign(); fetchAnalytics(); }, [fetchCampaign, fetchAnalytics]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([fetchCampaign(), fetchAnalytics()]);
      toast.success('Data refreshed');
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'recipients') fetchRecipients(1);
  }, [activeTab, fetchRecipients]);

  const doAction = async (action, fn) => {
    try { await fn(); fetchCampaign(); fetchAnalytics(); } catch (err) {
      toast.error(err?.response?.data?.error || `${action} failed`);
    }
  };

  const handleSend = async () => {
    setCostEstimate(null);
    setPlanQuota(planStatus || null);
    setCostLoading(true);
    setConfirmModal({ open: true, type: 'send' });
    try {
      const [costRes, latestPlanStatus] = await Promise.all([
        campaignApi.costEstimate(id).catch(() => null),
        refreshPlanStatus().catch(() => null)
      ]);
      if (costRes?.data?.success && costRes?.data?.data) {
        setCostEstimate(costRes.data.data);
      }
      setPlanQuota(latestPlanStatus || planStatus || null);
    } catch (err) {
      console.error('Send modal data failed:', err);
    } finally {
      setCostLoading(false);
    }
  };
  const handleDelete = () => setConfirmModal({ open: true, type: 'delete' });

  const handleConfirmAction = () => {
    const { type } = confirmModal;
    setConfirmModal({ open: false, type: '' });
    if (type === 'send') {
      doAction('send', async () => {
        const res = await campaignApi.send(id);
        if (res.data?.success) {
          const usage = res.data.usage || res.data.plan_usage;
          if (usage) {
            toast.success(`Campaign sent! ${Number(usage.emails_used || 0).toLocaleString()} of ${Number(usage.email_limit || 0).toLocaleString()} emails used this cycle`);
          } else {
            toast.success(res.data.message || 'Campaign queued for sending');
          }
          // Show info about unsubscribed filtering if provided
          if (res.data.info) {
            setTimeout(() => toast.info(res.data.info), 600);
          }
        }
      });
    } else if (type === 'delete') {
      doAction('delete', async () => {
        const res = await campaignApi.remove(id);
        if (res.data?.success) { toast.success('Deleted'); navigate('/dashboard/email/campaigns'); }
      });
    }
  };

  const handleTestSend = async () => {
    if (!testEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testEmail)) {
      toast.error('Enter a valid email'); return;
    }
    setTestSending(true);
    try {
      const res = await campaignApi.test(id, testEmail);
      if (res.data?.success) { toast.success(res.data.message || 'Test sent'); setTestEmail(''); }
      else toast.error(res.data?.error || 'Test failed');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Test failed');
    } finally { setTestSending(false); }
  };

  const fmtNum = (n) => (n ?? 0).toLocaleString();
  const fmtPct = (n) => `${parseFloat(n || 0).toFixed(1)}%`;
  const fmtDateShort = (d) => {
    if (!d) return '\u2014';
    return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };
  const timeAgo = (d) => {
    if (!d) return '';
    const diff = Date.now() - new Date(d).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  /* Format bytes to human-readable size */
  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  /* Get icon + color for a file based on MIME type */
  const getFileIcon = (type = '') => {
    if (type.startsWith('image/'))       return { Icon: Image,    color: '#3b82f6' };
    if (type === 'application/pdf')      return { Icon: FileText, color: '#ef4444' };
    if (type.includes('word') || type.includes('document')) return { Icon: FileText, color: '#2563eb' };
    if (type.includes('sheet') || type.includes('excel'))   return { Icon: FileText, color: '#16a34a' };
    return { Icon: File, color: '#6b7280' };
  };

  const st = STATUS_MAP[campaign?.status] || { label: campaign?.status, badge: 'neutral', icon: Mail };
  const isActionable = campaign?.status === 'draft' || campaign?.status === 'scheduled';

  /* Loading skeleton */
  if (loading) {
    return (
      <div className="ecd-page">
        <div className="em-skl" style={{ height: 40, width: 120, marginBottom: 24 }} />
        <div className="em-skl" style={{ height: 120, marginBottom: 24 }} />
        <div className="em-skl" style={{ height: 300 }} />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="ecd-page">
        <div className="em-empty">
          <h3>Campaign not found</h3>
          <p>The requested campaign could not be loaded.</p>
          <button className="em-btn em-btn-primary" onClick={() => navigate('/dashboard/email/campaigns')}>
            Back to Campaigns
          </button>
        </div>
      </div>
    );
  }

  const summary = analytics?.summary || {};

  const statCards = [
    { label: 'Recipients', value: fmtNum(campaign.total_recipients), rate: '', color: '#4f46e5', icon: Users },
    { label: 'Sent', value: fmtNum(campaign.sent_count), rate: '', color: '#6366f1', icon: Send },
    { label: 'Delivered', value: fmtNum(campaign.delivered_count), rate: fmtPct(summary.delivery_rate), color: '#10b981', icon: CheckCircle },
    { label: 'Opens', value: fmtNum(campaign.opened_count), rate: fmtPct(summary.open_rate), color: '#f59e0b', icon: Eye },
    { label: 'Clicks', value: fmtNum(campaign.clicked_count), rate: fmtPct(summary.click_rate), color: '#3b82f6', icon: MousePointerClick },
    ...(campaign.unsubscribed_count > 0 ? [{ label: 'Unsubscribed', value: fmtNum(campaign.unsubscribed_count), rate: '', color: '#8b5cf6', icon: UserX }] : []),
  ];

  return (
    <div className="ecd-page">
      {/* Header */}
      <header className="ecd-header">
        <div className="ecd-header-main">
          <div className="ecd-title-area">
            <button className="ecd-back-btn" onClick={() => navigate('/dashboard/email/campaigns')}>
              <ArrowLeft size={16} />
            </button>
            <h1 className="ecd-title">{campaign.name}</h1>
            <div className={`ecd-status-badge ecd-status-${st.badge}`}>
              <div className="ecd-status-dot" />
              {st.label}
            </div>
          </div>
          <div className="ecd-meta">
            <div className="ecd-meta-item">
              <Calendar size={14} />
              <span>Created on {fmtDateShort(campaign.created_at)}</span>
            </div>
            <div className="ecd-meta-dot" />
            <div className="ecd-meta-item">
              <Clock size={14} />
              <span>Updated {timeAgo(campaign.updated_at)}</span>
            </div>
          </div>
        </div>
        <div className="ecd-header-actions">
          <button className="em-btn em-btn-secondary" onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? <Loader2 size={16} className="spin-icon" /> : <RefreshCw size={16} />}
            <span>Refresh</span>
          </button>
          {isActionable && (
            <button className="em-btn em-btn-secondary" onClick={() => navigate(`/dashboard/email/campaigns/${id}/edit`)}>
              <Pencil size={16} />
              <span>Edit</span>
            </button>
          )}
          <button className="em-btn em-btn-secondary" onClick={() => {}}>
            <Copy size={16} />
            <span>Duplicate</span>
          </button>
          {isActionable && (
            <button className="em-btn em-btn-danger" onClick={handleDelete}>
              <Trash2 size={16} />
              <span>Delete</span>
            </button>
          )}
          {campaign.status === 'draft' && (
            <button className="em-btn em-btn-primary" onClick={handleSend}>
              <Send size={16} />
              <span>Send Campaign</span>
            </button>
          )}
        </div>
      </header>

      {/* Stat Cards */}
      <div className="ecd-stats-grid">
        {statCards.map(s => <StatCard key={s.label} {...s} />)}
      </div>

      {/* Tabs */}
      <div className="ecd-tabs-wrap">
        <div className="ecd-tabs">
          <TabButton id="overview" activeTab={activeTab} setActiveTab={setActiveTab}>Overview</TabButton>
          <TabButton id="preview" activeTab={activeTab} setActiveTab={setActiveTab}>Preview</TabButton>
          <TabButton id="recipients" activeTab={activeTab} setActiveTab={setActiveTab}>Recipients</TabButton>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {/* Campaign Info */}
          <div className="ecd-section">
            <div className="ecd-section-header"><h2 className="ecd-section-title">Campaign Details</h2></div>
            <div className="ecd-section-body">
              <div className="ecd-property-list">
                <div className="ecd-property-row">
                  <span className="ecd-property-label">Subject</span>
                  <span className="ecd-property-value">{campaign.subject || '—'}</span>
                </div>
                <div className="ecd-property-row">
                  <span className="ecd-property-label">From Name</span>
                  <span className="ecd-property-value">{campaign.from_name || '—'}</span>
                </div>
                <div className="ecd-property-row">
                  <span className="ecd-property-label">From Email</span>
                  <span className="ecd-property-value">{campaign.from_email || '—'}</span>
                </div>
                <div className="ecd-property-row">
                  <span className="ecd-property-label">Reply To</span>
                  <span className="ecd-property-value">{campaign.reply_to || campaign.from_email || '—'}</span>
                </div>
                <div className="ecd-property-row">
                  <span className="ecd-property-label">Template</span>
                  <span className="ecd-property-value">{campaign.template?.name || '—'}</span>
                </div>
                <div className="ecd-property-row">
                  <span className="ecd-property-label">Address Book</span>
                  <span className="ecd-property-value">{campaign.addressbook_name || '—'}</span>
                </div>
                <div className="ecd-property-row">
                  <span className="ecd-property-label">Sending Mode</span>
                  <span className="ecd-property-value" style={{ textTransform: 'capitalize' }}>{campaign.sending_mode || '—'}</span>
                </div>
                <div className="ecd-property-row">
                  <span className="ecd-property-label">Scheduled At</span>
                  <span className="ecd-property-value">{campaign.schedule_time ? fmtDateShort(campaign.schedule_time) : 'Immediate'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Attachments */}
          {campaign.template?.body_attachments?.length > 0 && (
            <div className="ecd-section">
              <div className="ecd-section-header">
                <h2 className="ecd-section-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Paperclip size={16} />
                  Attachments
                  <span className="ecd-attachment-count">{campaign.template.body_attachments.length}</span>
                </h2>
              </div>
              <div className="ecd-section-body">
                <div className="ecd-attachments-grid">
                  {campaign.template.body_attachments.map((att, i) => {
                    const { Icon, color } = getFileIcon(att.type);
                    const isImage = att.isImage || att.type?.startsWith('image/');
                    return (
                      <div key={i} className="ecd-attachment-card">
                        {isImage ? (
                          <div className="ecd-attachment-thumb">
                            <img src={att.url || att.downloadUrl} alt={att.name} className="ecd-attachment-img" />
                          </div>
                        ) : (
                          <div className="ecd-attachment-icon-wrap" style={{ background: `${color}14` }}>
                            <Icon size={24} color={color} />
                          </div>
                        )}
                        <div className="ecd-attachment-info">
                          <span className="ecd-attachment-name" title={att.name}>{att.name}</span>
                          <span className="ecd-attachment-meta">
                            {att.type?.split('/')[1]?.toUpperCase() || 'FILE'}
                            {att.size ? ` · ${formatFileSize(att.size)}` : ''}
                          </span>
                        </div>
                        <a
                          href={att.downloadUrl || att.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ecd-attachment-dl-btn"
                          title="Download"
                          download={att.name}
                        >
                          <Download size={15} />
                        </a>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Delivery Breakdown */}}
          <div className="ecd-section">
            <div className="ecd-section-header"><h2 className="ecd-section-title">Delivery Breakdown</h2></div>
            <div className="ecd-section-body">
              <div className="ecd-delivery-box">
                {[
                  { label: 'Delivered', count: campaign.delivered_count || 0, color: '#10b981' },
                  { label: 'Opened', count: campaign.opened_count || 0, color: '#f59e0b' },
                  { label: 'Clicked', count: campaign.clicked_count || 0, color: '#3b82f6' },
                  { label: 'Bounced', count: campaign.bounced_count || 0, color: '#ef4444' },
                  { label: 'Unsubscribed', count: campaign.unsubscribed_count || 0, color: '#8b5cf6' },
                  { label: 'Complained', count: campaign.complained_count || 0, color: '#ec4899' },
                  { label: 'Failed', count: campaign.failed_count || 0, color: '#667085' },
                ].map(item => {
                  const pct = campaign.total_recipients > 0
                    ? ((item.count / campaign.total_recipients) * 100).toFixed(1)
                    : 0;
                  return (
                    <div className="ecd-bar-row" key={item.label}>
                      <div className="ecd-bar-label">{item.label}</div>
                      <div className="ecd-bar-track-wrap">
                        <div className="ecd-bar-track">
                          <div className="ecd-bar-fill" style={{ width: `${Math.min(pct, 100)}%`, background: item.color }} />
                        </div>
                      </div>
                      <div className="ecd-bar-stat">
                        <span>{item.count.toLocaleString()}</span>
                        <span className="ecd-bar-pct">{pct}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'preview' && (
        <div className="ecd-preview-area">
          <div className={`ecd-preview-iframe-wrap ${previewMode}`}>
            <div className="ecd-preview-iframe-contain">
              <iframe
                ref={previewIframeRef}
                srcDoc={campaign.template
                  ? buildCampaignPreviewHtml(campaign.template)
                  : '<p style="font-family:sans-serif;padding:2rem;">No preview available.</p>'}
                title="Email Preview"
                className="ecd-preview-iframe"
              />
            </div>
          </div>
          <div className="ecd-preview-controls">
            <h3 className="ecd-preview-control-title">View Mode</h3>
            <div className="ecd-device-toggle">
              <button className={`ecd-device-btn ${previewMode === 'desktop' ? 'active' : ''}`} onClick={() => setPreviewMode('desktop')}>
                <Monitor size={16} />
                <span>Desktop</span>
              </button>
              <button className={`ecd-device-btn ${previewMode === 'mobile' ? 'active' : ''}`} onClick={() => setPreviewMode('mobile')}>
                <Smartphone size={16} />
                <span>Mobile</span>
              </button>
            </div>
            <h3 className="ecd-preview-control-title" style={{ marginTop: 32 }}>Send Test Email</h3>
            <div className="ecd-modal-input-group">
              <input
                type="email"
                className="em-input"
                placeholder="Enter email address"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
              />
              <button className="em-btn em-btn-primary" onClick={handleTestSend} disabled={testSending}>
                {testSending ? <Loader2 size={16} className="spin-icon" /> : <Send size={16} />}
                <span>Send Test</span>
              </button>
            </div>
            {/* Attached Files in preview sidebar */}
            {campaign.template?.body_attachments?.length > 0 && (
              <>
                <h3 className="ecd-preview-control-title" style={{ marginTop: 32, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Paperclip size={14} />
                  Attached Files ({campaign.template.body_attachments.length})
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {campaign.template.body_attachments.map((att, i) => {
                    const { Icon, color } = getFileIcon(att.type);
                    return (
                      <div key={i} className="ecd-preview-att-chip">
                        <Icon size={14} color={color} style={{ flexShrink: 0 }} />
                        <span className="ecd-preview-att-name" title={att.name}>{att.name}</span>
                        <span className="ecd-preview-att-size">{formatFileSize(att.size)}</span>
                        <a href={att.downloadUrl || att.url} target="_blank" rel="noopener noreferrer" download={att.name} className="ecd-preview-att-dl">
                          <ExternalLink size={12} />
                        </a>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {activeTab === 'recipients' && (
        <div className="ecd-section">
          <div className="ecd-section-header"><h2 className="ecd-section-title">Recipients</h2></div>
          <div className="ecd-section-body" style={{ padding: 0 }}>
            <div className="ecd-recipients-filters" style={{ padding: '1.5rem', marginBottom: 0 }}>
              <div className="ecd-search-wrap">
                <Search size={16} className="ecd-search-icon" />
                <input
                  className="em-input ecd-search"
                  placeholder="Search recipients..."
                  value={recipientsSearch}
                  onChange={e => setRecipientsSearch(e.target.value)}
                />
              </div>
              <select
                className="ecd-select"
                value={recipientsStatus}
                onChange={e => setRecipientsStatus(e.target.value)}
                style={{ width: 180 }}
              >
                <option value="">All Statuses</option>
                <option value="sent">Sent</option>
                <option value="delivered">Delivered</option>
                <option value="opened">Opened</option>
                <option value="clicked">Clicked</option>
                <option value="failed">Failed</option>
                <option value="bounced">Bounced</option>
                <option value="skipped_unsubscribed">Unsubscribed</option>
              </select>
            </div>
            {recipientsLoading ? (
              <div style={{ height: 300, display: 'grid', placeContent: 'center' }}><Loader2 className="spin-icon" size={32} color="#667085" /></div>
            ) : recipients.length === 0 ? (
              <div className="em-empty" style={{ borderTop: '1px solid #eaecf0', padding: '3rem' }}>
                <p style={{ color: '#475467', margin: 0 }}>No recipients found.</p>
              </div>
            ) : (
              <>
                <div className="em-table-wrap" style={{ margin: 0, borderTop: '1px solid #eaecf0' }}>
                  <table className="em-table">
                    <thead>
                      <tr>
                        <th>Email Address</th>
                        <th>Status</th>
                        <th>Reason</th>
                        <th>Opened</th>
                        <th>Clicked</th>
                        <th>Last Activity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recipients.map(r => (
                        <tr key={r.id}>
                          <td style={{ fontWeight: 500 }}>{r.email_address}</td>
                          <td>
                            <span className={`ecd-status-badge ecd-status-${getRecipientBadge(r.status)}`}>
                              <div className="ecd-status-dot" />
                              {r.status === 'skipped_unsubscribed' ? 'Unsubscribed' : r.status}
                            </span>
                          </td>
                          <td style={{ color: r.status === 'skipped_unsubscribed' ? '#7c3aed' : '#b42318', fontSize: '0.8rem', maxWidth: 280, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                            {r.status === 'skipped_unsubscribed'
                              ? (r.error_message || 'Unsubscribed — not counted against quota')
                              : (r.fail_reason || r.error_message || '\u2014')}
                          </td>
                          <td>{r.opened_at ? 'Yes' : 'No'}</td>
                          <td>{r.clicked_at ? 'Yes' : 'No'}</td>
                          <td style={{ color: '#475467' }}>{timeAgo(r.failed_at || r.clicked_at || r.opened_at || r.delivered_at || r.sent_at || r.updated_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {recipientsPaging.totalPages > 1 && (
                  <div className="em-pagination" style={{ padding: '1rem 1.5rem' }}>
                    <span className="em-pagination-info" style={{ color: '#475467', fontSize: '0.875rem' }}>
                      Page {recipientsPaging.page} of {recipientsPaging.totalPages}
                    </span>
                    <div className="em-pagination-btns">
                      <button className="em-page-btn" disabled={recipientsPaging.page <= 1} onClick={() => fetchRecipients(recipientsPaging.page - 1)} style={{ border: '1px solid #d0d5dd', background: '#fff', borderRadius: '8px', padding: '6px 12px', cursor: 'pointer' }}>Previous</button>
                      <button className="em-page-btn" disabled={recipientsPaging.page >= recipientsPaging.totalPages} onClick={() => fetchRecipients(recipientsPaging.page + 1)} style={{ border: '1px solid #d0d5dd', background: '#fff', borderRadius: '8px', padding: '6px 12px', cursor: 'pointer', marginLeft: '8px' }}>Next</button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Confirm Modals */}
      <ConfirmModal
        open={confirmModal.open}
        variant={confirmModal.type === 'delete' ? 'danger' : 'primary'}
        title={`${confirmModal.type === 'delete' ? 'Delete' : 'Send'} Campaign`}
        message={confirmModal.type === 'delete' ? 'Are you sure you want to delete this campaign? This action cannot be undone.' : null}
        confirmLabel={confirmModal.type === 'delete' ? 'Delete' : (() => {
          const quota = planQuota?.email || costEstimate?.email_quota;
          const remaining = quota?.remaining || 0;
          const recipients = Number(costEstimate?.recipients || campaign?.total_recipients || 0);
          return remaining < recipients ? 'Insufficient Quota' : 'Send Now';
        })()}
        confirmDisabled={confirmModal.type === 'send' && (() => {
          const quota = planQuota?.email || costEstimate?.email_quota;
          const remaining = quota?.remaining || 0;
          const recipients = Number(costEstimate?.recipients || campaign?.total_recipients || 0);
          return remaining < recipients;
        })()}
        onConfirm={handleConfirmAction}
        onCancel={() => { setConfirmModal({ open: false, type: '' }); setCostEstimate(null); setPlanQuota(null); }}
      >
        {confirmModal.type === 'send' && (
          costLoading ? (
            <div style={{ textAlign: 'center', padding: '12px 0' }}>
              <span className="spin-icon" style={{ display: 'inline-flex' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
              </span>
              <p style={{ margin: '8px 0 0', fontSize: 13, color: '#6b7280' }}>Calculating cost...</p>
            </div>
          ) : (costEstimate || planQuota) ? (
            <div style={{ margin: '12px 0 0', textAlign: 'left', background: '#f9fafb', borderRadius: 8, padding: '14px 16px', fontSize: 13, lineHeight: 1.8 }}>
              {costEstimate?.campaign_name && (
                <div style={{ fontWeight: 600, marginBottom: 4, color: '#111827' }}>{costEstimate.campaign_name}</div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#6b7280' }}>Recipients</span><span style={{ fontWeight: 600 }}>{Number(costEstimate?.recipients || campaign?.total_recipients || 0).toLocaleString()}</span></div>
              {(() => {
                const quota = planQuota?.email || costEstimate?.email_quota;
                const remaining = quota?.remaining || 0;
                const recipients = Number(costEstimate?.recipients || campaign?.total_recipients || 0);
                const afterSend = Math.max(0, remaining - recipients);
                const hasInsufficient = remaining < recipients;
                return (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#6b7280' }}>Email Quota</span><span style={{ fontWeight: 600, color: hasInsufficient ? '#dc2626' : '#059669' }}>{remaining.toLocaleString()} remaining</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#6b7280' }}>After Send</span><span style={{ fontWeight: 600, color: hasInsufficient ? '#dc2626' : '#059669' }}>{afterSend.toLocaleString()} remaining</span></div>
                    {hasInsufficient && (
                      <div style={{ marginTop: 8, padding: '8px 10px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#dc2626', fontSize: 12, fontWeight: 500 }}>
                        Insufficient email quota! You need {recipients.toLocaleString()} emails but only {remaining.toLocaleString()} remain.
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          ) : (
            <p style={{ margin: '8px 0 0', fontSize: 13, color: '#6b7280' }}>Send this campaign? Emails will begin delivering immediately.</p>
          )
        )}
      </ConfirmModal>
    </div>
  );
};

function getRecipientBadge(status) {
  const map = {
    pending: 'neutral', sent: 'info', delivered: 'success',
    opened: 'primary', clicked: 'primary', bounced: 'danger', failed: 'danger',
    skipped_unsubscribed: 'warning',
  };
  return map[status] || 'neutral';
}

export default EmailCampaignDetail;
